import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { CatalogItemData, QuoteView } from "@contracts/index";
import { addDays, DEFAULT_VALIDITY_DAYS } from "@domain/folio";
import { ErrorBox } from "../../components/AdminLayout";
import { TotalsBox } from "../../components/TotalsBox";
import { api, errorMessage } from "../../lib/api";
import { COMMON_COUNTRIES, COMMON_CURRENCIES } from "../../lib/format";
import {
  emptyForm,
  emptyItem,
  formFromQuote,
  itemFromCatalog,
  previewTotals,
  toEditableFields,
  withCountry,
  type ContactForm,
  type ItemForm,
  type QuoteForm,
} from "../../lib/quote-form";

/** Creates a new quote (no `quote`) or edits a draft. */
export function QuoteEditor({ quote, onSaved }: { quote?: QuoteView; onSaved?: (q: QuoteView) => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState<QuoteForm>(() =>
    quote ? formFromQuote(quote) : emptyForm(addDays(new Date(), DEFAULT_VALIDITY_DAYS)),
  );
  const [catalog, setCatalog] = useState<CatalogItemData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const totals = useMemo(() => previewTotals(form), [form]);

  useEffect(() => {
    api.admin.listCatalog(true).then(setCatalog).catch(() => setCatalog([]));
  }, []);

  const set = <K extends keyof QuoteForm>(key: K, value: QuoteForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setItem = (index: number, patch: Partial<ItemForm>) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === index ? { ...it, ...patch } : it)) }));
  const setContact = (index: number, patch: Partial<ContactForm>) =>
    setForm((f) => ({ ...f, contacts: f.contacts.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));
  const moveItem = (index: number, delta: number) =>
    setForm((f) => {
      const items = [...f.items];
      const target = index + delta;
      if (target < 0 || target >= items.length) return f;
      [items[index], items[target]] = [items[target]!, items[index]!];
      return { ...f, items };
    });

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const fields = toEditableFields(form);
      const saved = quote ? await api.admin.updateQuote(quote.id, fields) : await api.admin.createQuote(fields);
      if (quote) onSaved?.(saved);
      else navigate(`/admin/quotes/${saved.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function addFromCatalog(id: string) {
    const entry = catalog.find((c) => c.id === id);
    if (!entry) return;
    setForm((f) => ({ ...f, items: [...f.items.filter((it) => it.concept.trim()), itemFromCatalog(entry, f.currency)] }));
  }

  return (
    <form onSubmit={onSubmit} className="editor">
      <section className="card">
        <h2>General</h2>
        <div className="grid">
          <label>
            Título
            <input required value={form.title} onChange={(e) => set("title", e.target.value)} />
          </label>
          <label>
            Idioma
            <select value={form.language} onChange={(e) => set("language", e.target.value as QuoteForm["language"])}>
              <option value="es">Español</option>
              <option value="en">Inglés</option>
            </select>
          </label>
          <label>
            Moneda
            <input
              list="currencies"
              required
              maxLength={3}
              value={form.currency}
              onChange={(e) => set("currency", e.target.value.toUpperCase())}
            />
            <datalist id="currencies">
              {COMMON_CURRENCIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
          <label>
            Vigencia hasta
            <input type="date" required value={form.validUntil} onChange={(e) => set("validUntil", e.target.value)} />
          </label>
          <label>
            Forma de cobro
            <select
              value={form.paymentMethod}
              onChange={(e) => set("paymentMethod", e.target.value as QuoteForm["paymentMethod"])}
            >
              <option value="shopify">Shopify (pedido con enlace de pago)</option>
              <option value="transfer">Transferencia / factura</option>
            </select>
          </label>
          <label>
            Impuestos
            <select
              value={form.taxMode}
              onChange={(e) =>
                setForm((f) => ({ ...f, taxMode: e.target.value as QuoteForm["taxMode"], taxModeTouched: true }))
              }
            >
              <option value="iva16">IVA 16 %</option>
              <option value="exempt">Exento</option>
            </select>
          </label>
        </div>
      </section>

      <section className="card">
        <h2>Cliente</h2>
        <div className="grid">
          <label>
            Empresa
            <input required value={form.clientName} onChange={(e) => set("clientName", e.target.value)} />
          </label>
          <label>
            País
            <input
              list="countries"
              required
              maxLength={2}
              value={form.clientCountry}
              onChange={(e) => setForm((f) => withCountry(f, e.target.value))}
            />
            <datalist id="countries">
              {COMMON_COUNTRIES.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </datalist>
          </label>
          <label>
            RFC / Tax ID (opcional)
            <input value={form.clientTaxId} onChange={(e) => set("clientTaxId", e.target.value)} />
          </label>
        </div>

        <h3>Contactos con acceso</h3>
        {form.contacts.map((contact, i) => (
          <div className="row" key={i}>
            <input placeholder="Nombre" value={contact.name} onChange={(e) => setContact(i, { name: e.target.value })} />
            <input
              type="email"
              placeholder="correo@empresa.com"
              value={contact.email}
              onChange={(e) => setContact(i, { email: e.target.value })}
            />
            <input placeholder="Puesto (opcional)" value={contact.role} onChange={(e) => setContact(i, { role: e.target.value })} />
            <button
              type="button"
              className="link"
              onClick={() => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, j) => j !== i) }))}
            >
              Quitar
            </button>
          </div>
        ))}
        <button
          type="button"
          className="secondary"
          onClick={() => setForm((f) => ({ ...f, contacts: [...f.contacts, { name: "", email: "", role: "" }] }))}
        >
          + Contacto
        </button>
      </section>

      <section className="card">
        <h2>Partidas</h2>
        {form.items.map((item, i) => (
          <div className="item" key={item.id}>
            <div className="row">
              <input
                className="grow"
                placeholder="Concepto"
                value={item.concept}
                onChange={(e) => setItem(i, { concept: e.target.value })}
              />
              <input
                className="narrow"
                inputMode="decimal"
                placeholder="Cant."
                value={item.quantity}
                onChange={(e) => setItem(i, { quantity: e.target.value })}
              />
              <input
                className="narrow"
                inputMode="decimal"
                placeholder={`Precio ${form.currency}`}
                value={item.unitPrice}
                onChange={(e) => setItem(i, { unitPrice: e.target.value })}
              />
            </div>
            <textarea
              rows={2}
              placeholder="Descripción"
              value={item.description}
              onChange={(e) => setItem(i, { description: e.target.value })}
            />
            <div className="row small">
              <label className="inline">
                <input type="checkbox" checked={item.optional} onChange={(e) => setItem(i, { optional: e.target.checked })} />
                Opcional
              </label>
              {item.optional && (
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={(e) => setItem(i, { selected: e.target.checked })}
                  />
                  Incluida en el total
                </label>
              )}
              <span className="spacer" />
              <button type="button" className="link" onClick={() => moveItem(i, -1)} aria-label="Subir">
                ↑
              </button>
              <button type="button" className="link" onClick={() => moveItem(i, 1)} aria-label="Bajar">
                ↓
              </button>
              <button
                type="button"
                className="link"
                onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}
              >
                Quitar
              </button>
            </div>
          </div>
        ))}
        <div className="row">
          <button type="button" className="secondary" onClick={() => set("items", [...form.items, emptyItem()])}>
            + Partida libre
          </button>
          {catalog.length > 0 && (
            <select value="" onChange={(e) => addFromCatalog(e.target.value)}>
              <option value="">+ Agregar del catálogo…</option>
              {catalog.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.concept}
                  {c.prices[form.currency] === undefined ? ` (sin precio en ${form.currency})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
        <TotalsBox totals={totals} language={form.language} />
      </section>

      <section className="card">
        <h2>Textos</h2>
        <label>
          Introducción
          <textarea rows={4} value={form.intro} onChange={(e) => set("intro", e.target.value)} />
        </label>
        <label>
          Condiciones
          <textarea rows={6} value={form.terms} onChange={(e) => set("terms", e.target.value)} />
        </label>
      </section>

      <ErrorBox message={error} />
      <div className="actions">
        <button className="primary" type="submit" disabled={saving}>
          {saving ? "Guardando…" : quote ? "Guardar cambios" : "Crear cotización"}
        </button>
      </div>
    </form>
  );
}
